import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { AppSettings } from 'pac-api';
import { Observable, of } from 'rxjs';
import { catchError, map, share } from 'rxjs/operators';

import { CatchError, empty } from '../helpers';
import { LoggerService } from '../logger.service';
import { AddressModel } from '../model/address.model';
import { ApiError } from '../model/api-error';
import { CountryModel } from '../model/country.model';
import { IndividualAddressModel } from '../model/individual-address.model';
import { WizardService } from '../wizard/wizard.service';
import { AddressFormatModel } from './dynamic-address/address-format.model';

@Injectable({
  providedIn: 'root'
})
export class AddressService {
  private _isSaving: boolean;

  private administrative_areas: {[key: string]: [{ sta_name: string; sta_code: string; }]};

  private mailing_label_format: {[key: string]: string[]} = {};

  private address_format: {[key: string]: AddressFormatModel} = {};

  private countries: CountryModel[];

  public get Countries(): CountryModel[] {
    if (this.countries === null || this.countries === undefined) {
      this.countries = [];
      for (const value of this.settings.countries) {
        const country = new CountryModel(value);
        this.countries.push(country);
      }
    }

    return this.countries;
  }

  public group: UntypedFormGroup;

  public validated: AddressModel | IndividualAddressModel;

  public original: AddressModel | IndividualAddressModel;

  public get isSaving(): boolean {
    return this._isSaving;
  }

  public set isSaving(state: boolean) {
    if (this.group) {
      if (state && this.group.enabled) {
        this.group.disable({onlySelf: false, emitEvent: true});
      } else if (!state && this.group.disabled) {
        this.group.enable({onlySelf: false, emitEvent: true});
      }
    }

    this._isSaving = state;
  }

  constructor(
    public wizard: WizardService,
    private http: HttpClient,
    private logger: LoggerService,
    private settings: AppSettings,
  ) {
    this.administrative_areas = {};

    this.group = new UntypedFormGroup({
      // Hidden fields
      cxa_key: new UntypedFormControl(null),
      cxa_on_hold_flag: new UntypedFormControl(null),
      cxa_primary: new UntypedFormControl(null),
      cxa_billing: new UntypedFormControl(null),
      cxa_adt_key: new UntypedFormControl(null),

      adr_county: new UntypedFormControl(null),
      adr_intl_province: new UntypedFormControl(null),
      adr_latitude: new UntypedFormControl(null),
      adr_longitude: new UntypedFormControl(null),
      adr_cong_dist: new UntypedFormControl(null),
      adr_carrier_route: new UntypedFormControl(null),
      adr_fips: new UntypedFormControl(null),
      adr_time_zone: new UntypedFormControl(null),
      adr_delivery_point_code: new UntypedFormControl(null)
    });
  }

  public Form2Model(values: any): IndividualAddressModel | AddressModel {
    const address = Object.assign(values);

    if (values.adr_country && values.adr_country.cty_code) {
      address.adr_country = values.adr_country.cty_code;
    }

    if (values.adr_state && values.adr_state.sta_code) {
      address.adr_state = values.adr_state.sta_code;
    }

    if ('cxa_key' in address && !empty(address.cxa_key)) {
      return new IndividualAddressModel(address);
    } else {
      return new AddressModel(address);
    }
  }

  public GetCountry(search: string): CountryModel {
    return this.Countries.find(country => {
      if (search.length === 2) {
        return country.cty_iso_2 === search;
      } else {
        return country.cty_code === search;
      }
    });
  }

  public Validate() {
    const address = this.Form2Model(this.group.value);

    if (this.original === null || this.original === undefined) {
      this.original = address;
    }

    const observable = this.http.patch<any>(
      this.settings.pac_base_nf_url + 'api/Address',
      address,
      {
        withCredentials: true
      }
    );

    const shared = observable.pipe(catchError((err: HttpErrorResponse) => {
      if (err.error.error === true) {
        throw new ApiError(err.error.message, err.error.code || err.status);
      }

      if (err.error) {
        throw err.error;
      }

      if (err.statusText && err.statusText !== 'OK') {
        throw new Error(err.statusText);
      }

      throw err;
    })).pipe(map((result: any) => {
      if (result.error === true) {
        throw new Error(result.message);
      } else if ('adr_key' in result || 'adr_line1' in result || 'adr_country' in result) {
        if (address instanceof IndividualAddressModel) {
          const cxa = new IndividualAddressModel(address);
          return Object.assign(cxa, result);
        } else {
          return new AddressModel(result);
        }
      } else {
        throw new Error('Unknown response');
      }
    }), share<AddressModel | IndividualAddressModel>());

    shared.subscribe(result => {
      try {
        this.logger.info(result);
      } catch {}
      if (result instanceof AddressModel) {
        this.validated = result;
      }
    });

    return shared;
  }

  public GetFormat(country: string): Observable<string[]> {
    if (country === undefined || country === null || country === '') {
      return null;
    }

    if (country in this.mailing_label_format && this.mailing_label_format[country] instanceof Array) {
      return of(this.mailing_label_format[country]);
    }

    const oberserable = this.http.get<any>(
      this.settings.pac_base_nf_url + 'api/Helper/co_country_mailing_label_format_select/' + country,
      {
        withCredentials: true
      }
    );

    const shared = oberserable.pipe(catchError((err: HttpErrorResponse) => {
      if (err.error.error === true) {
        throw new ApiError(err.error.message, err.error.code || err.status);
      }

      if (err.error) {
        throw err.error;
      }

      if (err.statusText && err.statusText !== 'OK') {
        throw new Error(err.statusText);
      }
      throw err;
    })).pipe(map((result: any) => {
      if (result.error === true) {
        throw new Error(result.message);
      } else if (typeof(result) === 'string') {
        return result.split('\r\n');
      } else {
        throw new Error('Unknown response');
      }
    }), share());

    shared.subscribe(result => {
      try {
        this.logger.info(result);
      } catch {}
    });

    shared.subscribe(result => {
      this.mailing_label_format[country] = result.map(line => line.trim());
    });

    return shared;
  }

  public GetAddressFormat(country: string | CountryModel): Observable<AddressFormatModel> {
    if (country === null || country === undefined) {
      return null;
    }

    let _country: CountryModel;

    if (typeof country === 'string') {
     _country = this.GetCountry(country);
    } else if (country instanceof CountryModel) {
      _country = country;
    } else {
      return null;
    }

    if (_country && _country.cty_iso_2 in this.address_format && typeof this.address_format[_country.cty_iso_2].format === 'object') {
      return of(this.address_format[_country.cty_iso_2]);
    }

    const oberserable = this.http.get<AddressFormatModel>(`/pac-wizard/country/${_country.cty_iso_2}`);

    this.GetFormat(_country.cty_code);

    const shared = oberserable.pipe(share());
    shared.subscribe(result => {
      try {
        this.logger.info(result);
      } catch {}
    });

    shared.subscribe(result => {
      this.SetAdministrativeAreas(_country.cty_code, result.administrative_areas);

      this.address_format[_country.cty_code] = result;
    });

    return shared;
  }

  public SetAdministrativeAreas(country: string, administrative_areas: [{ sta_name: string; sta_code: string; }]): void {
    this.administrative_areas[country] = administrative_areas;
  }

  public GetAdministrativeAreas(country: string) {
    return this.administrative_areas[country];
  }

  public GetAdministrativeArea(country: string, state: string): ({ sta_name: string; sta_code: string; }) {
    return this.GetAdministrativeAreas(country).find((sta: any) => sta.sta_code === state);
  }

  public SaveAddress(address: IndividualAddressModel | AddressModel): Observable<IndividualAddressModel> {

    let observable: Observable<IndividualAddressModel> = null;
    let individualAddress: IndividualAddressModel;

    if (address instanceof IndividualAddressModel) {
      individualAddress = address;
    } else {
      individualAddress = new IndividualAddressModel(address);
    }

    if (empty(this.wizard.Individual.cxa_key)) {
      individualAddress.cxa_primary = true;
    }

    if (empty(this.wizard.Individual.cst_cxa_billing_key)) {
      individualAddress.cxa_billing = true;
    }

    if (! empty(individualAddress.cxa_key)) {
      observable = this.http.put<IndividualAddressModel>(
        this.settings.pac_base_nf_url + 'api/Address/' + individualAddress.cxa_key,
        individualAddress,
        {
          withCredentials: true
        }
      );
    } else {
      observable = this.http.post<IndividualAddressModel>(
        this.settings.pac_base_nf_url + 'api/Address',
        individualAddress,
        {
          withCredentials: true
        }
      );
    }

    const shared = observable.pipe(catchError(CatchError)).pipe(map((result: any) => {
      if (result.error === true) {
        throw new Error(result.message);
      } else if ('adr_key' in result || 'adr_line1' in result || 'adr_country' in result) {
        return new IndividualAddressModel(result);
      } else {
        throw new Error('Unknown response');
      }
    }), share());

    shared.subscribe(result => {
      try {
        this.logger.info(result);
      } catch {}
    });

    return shared;
  }

  public GetAddress(id: string): Observable<IndividualAddressModel> {
    const observable = this.http.get<IndividualAddressModel>(
      this.settings.pac_base_nf_url + 'api/Address/' + id,
      {
        withCredentials: true
      }
    );

    const shared = observable.pipe(catchError((err: HttpErrorResponse) => {
      if (err.error.error === true) {
        throw new ApiError(err.error.message, err.error.code || err.status);
      }

      if (err.error) {
        throw err.error;
      }

      if (err.statusText && err.statusText !== 'OK') {
        throw new Error(err.statusText);
      }

      throw err;
    })).pipe(map((result: any) => {
      if (result.error === true) {
        throw new Error(result.message);
      } else if ('adr_key' in result || 'adr_line1' in result || 'adr_country' in result) {
        return new IndividualAddressModel(result);
      } else {
        throw new Error('Unknown response');
      }
    }), share());

    shared.subscribe(result => {
      try {
        this.logger.info(result);
      } catch {}
    });

    return shared;
  }
}
